import {
  HelloResponseSchema,
  type HelloResponse,
  NoteSchema,
  NotesResponseSchema,
  type Note,
  type CreateNoteInput,
  ProductSchema,
  ProductsResponseSchema,
  type Product,
  type ProductInput,
} from '~shared/schemas';

/**
 * Типизированный API-клиент.
 *
 * Паттерн каждого метода:
 *   1. fetch() → берём JSON.
 *   2. ZodSchema.parse() — валидируем, что сервер вернул именно то, что обещал.
 *   3. Возвращаем типизированное значение.
 *
 * Ошибки:
 *   - Не-2xx кидает ApiError с status и body.
 *   - Невалидный JSON кидает ZodError — react-query ловит автоматически.
 */

export class ApiError extends Error {
  constructor(public readonly status: number, public readonly body: unknown) {
    super(`HTTP ${status}`);
    this.name = 'ApiError';
  }
}

async function request<T>(
  path: string,
  schema: { parse: (x: unknown) => T },
  init?: RequestInit,
): Promise<T> {
  const res = await fetch(path, init);
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new ApiError(res.status, body);
  }
  const json = await res.json();
  return schema.parse(json);
}

function postJson<T>(path: string, schema: { parse: (x: unknown) => T }, body: unknown): Promise<T> {
  return request(path, schema, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

function putJson<T>(path: string, schema: { parse: (x: unknown) => T }, body: unknown): Promise<T> {
  return request(path, schema, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

async function del(path: string): Promise<void> {
  const res = await fetch(path, { method: 'DELETE' });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new ApiError(res.status, body);
  }
}

export const api = {
  hello(): Promise<HelloResponse> {
    return request('/api/hello', HelloResponseSchema);
  },
  notes: {
    list(): Promise<Note[]> {
      return request('/api/notes', NotesResponseSchema);
    },
    get(id: number): Promise<Note> {
      return request(`/api/notes/${id}`, NoteSchema);
    },
    create(input: CreateNoteInput): Promise<Note> {
      return postJson('/api/notes', NoteSchema, input);
    },
  },
  products: {
    list(): Promise<Product[]> {
      return request('/api/products', ProductsResponseSchema);
    },
    create(input: ProductInput): Promise<Product> {
      return postJson('/api/products', ProductSchema, input);
    },
    update(id: number, input: ProductInput): Promise<Product> {
      return putJson(`/api/products/${id}`, ProductSchema, input);
    },
    delete(id: number): Promise<void> {
      return del(`/api/products/${id}`);
    },
  },
};
